import type { SkillResult } from "../hooks/useSkills";

export function resultCsv(result: SkillResult): string {
  // Spreadsheet exports must not execute page text as a formula.
  const cell = (value: string) => `"${(/^[=+@\-\t\r]/.test(value) ? "'" + value : value).replace(/"/g, '""')}"`;
  const fields = Object.keys(result.data);
  const rows = Math.max(1, ...Object.values(result.data).map(v => Array.isArray(v) ? v.length : 1));
  return [fields.map(cell).join(","), ...Array.from({ length: rows }, (_, i) => fields.map(key => {
    const value = result.data[key]; return cell(Array.isArray(value) ? value[i] ?? "" : i === 0 ? value ?? "" : "");
  }).join(","))].join("\r\n");
}

export function download(result: SkillResult, format: "json" | "csv") {
  const blob = new Blob([format === "json" ? JSON.stringify(result, null, 2) : resultCsv(result)],
    { type: format === "json" ? "application/json" : "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob); const link = document.createElement("a");
  link.href = url; link.download = `skill-result.${format}`; link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Bounded text handed to the Chat: the data travel only on an explicit click. */
export function chatPrompt(title: string, result: SkillResult): string {
  const csv = resultCsv(result);
  return `Voici le résultat du skill « ${title} », capturé le ${new Date(result.captured_at).toLocaleString("fr-FR")} sur ${result.source_url}. Aide-moi à l’analyser.\n\n`
    + (csv.length > 6000 ? csv.slice(0, 6000) + "\n… (tronqué)" : csv);
}

export const fieldLabel = (key: string) => {
  const country = key.match(/^country_(\d+)_(code|keywords|change)$/);
  if (country) return `Pays ${country[1]}${country[2] === "keywords" ? " · Mots-clés" : country[2] === "change" ? " · Évolution" : ""}`;
  return ({ project_name: "Projet", domain_pattern: "Domaine", organic_traffic: "Trafic organique", organic_keywords: "Mots-clés organiques", referring_domains: "Domaines référents",
    domain_rating: "Autorité du domaine", health_score: "Santé du site", crawled_pages: "Pages explorées", redirects: "Redirections", broken_links: "Liens cassés", blocked_pages: "Pages bloquées",
    traffic_value: "Valeur du trafic", other_countries_keywords: "Autres pays · Mots-clés", other_countries_change: "Autres pays · Évolution" } as Record<string, string>)[key] ?? key.replace(/_/g, " ");
};
const orderedFields = (fields: string[]) => [...fields.filter(key => key === "project_name"), ...fields.filter(key => key === "domain_pattern"), ...fields.filter(key => key !== "project_name" && key !== "domain_pattern")];

export function ResultView({ result }: { result: SkillResult }) {
  const groups = Object.entries(result.row_groups ?? {}).filter(([, keys]) => keys.length > 1 && keys.every(key => Array.isArray(result.data[key]))).map(([group, keys]) => [group, orderedFields(keys)] as const);
  const tableFields = new Set(groups.flatMap(([, keys]) => keys));
  return <div className="skills-result">
    {groups.map(([group, keys]) => <div key={group} className="skills-table"><table aria-label="Données capturées">
      <caption>{(result.data[keys[0]!] as string[]).length} lignes capturées</caption><thead><tr>{keys.map(key => <th scope="col" key={key}>{fieldLabel(key)}</th>)}</tr></thead>
      <tbody>{(result.data[keys[0]!] as string[]).map((_, i) => <tr key={i}>{keys.map(key => <td key={key}>{(result.data[key] as string[])[i]}</td>)}</tr>)}</tbody>
    </table></div>)}
    <dl>{Object.entries(result.data).filter(([key]) => !tableFields.has(key)).map(([key, value]) => <div key={key}><dt>{fieldLabel(key)}</dt><dd>{Array.isArray(value) ? value.join("\n") : value}</dd></div>)}</dl>
    <details><summary>Résultat et provenance</summary><pre>{JSON.stringify(result, null, 2)}</pre></details>
  </div>;
}
